/**
 * CONTRACT TYPE REGISTRY - Single Source of Truth
 * ================================================
 * Todas as regras comportamentais de cada tipo de contrato
 * estão centralizadas aqui.
 *
 * ARQUITETURA:
 * - Array hardcoded (CONTRACT_TYPES) = defaults/fallback
 * - DB metadata (tipos_contrato.metadata) = override dinâmico
 * - ensureRegistryLoaded() carrega do DB e faz merge
 * - Cache em memória com TTL de 60s
 * - API síncrona preservada: getContractType() etc.
 *
 * IMPORTANTE: Este arquivo usa CONTCONF (id_registro), não o ID da tabela!
 */

import type { CategoriaContrato } from '@/types/contratos'

// ============================================================================
// TIPOS
// ============================================================================

export type TipoNegocio = 'Originacao' | 'Intermediacao' | 'Assessoria' | 'Assessoria espelho'
export type Moeda = 'BRL' | 'USD'

/**
 * Estratégia de resolução de IDs (numerocm / numerocmadic)
 *
 * - separate: vendedor e comprador distintos (Intermediação)
 * - both_cliente: ambos = cliente_id (Assessoria, Compra)
 * - both_comprador: ambos = comprador_id (Venda, Venda a Fixar)
 */
export type IdLogic = 'separate' | 'both_cliente' | 'both_comprador'

/**
 * Estratégia de cálculo de valores para payload
 *
 * - normal: valorunit = precocont/60
 * - intermediacao: quantidade=1, valorunit=comissão total
 * - assessoria: quantidade=hectares*kg/ha, valorunit=1
 */
export type ValueStrategy = 'normal' | 'intermediacao' | 'assessoria' | 'servico_mesa'

/**
 * Estratégia de cálculo de datas (dtvencto, dtlimliqimp)
 *
 * - originacao: dtPagamento direto
 * - intermediacao: dtPagamento + 3 dias
 * - assessoria_contconf4: 30/05 baseado no mês de fechamento
 * - assessoria_espelho: 30/05 baseado na dtLimiteEntrega
 */
export type DateStrategy = 'originacao' | 'intermediacao' | 'assessoria_contconf4' | 'assessoria_espelho'

/**
 * Resolução do item para API
 *
 * - fixed: item fixo (Intermediação=12, Assessoria=13)
 * - dynamic_variant: item vem de tipos_produto_variante.item_code
 */
export type ItemResolution = 'fixed' | 'dynamic_variant'

/**
 * Regra do pagadorcont
 *
 * - innovagro: fixo 'INNOVAGRO' (Compra)
 * - cliente_nome: cliente_nome.toUpperCase() (todos os outros)
 */
export type PagadorContRule = 'innovagro' | 'cliente_nome'

/**
 * Modo de aprovação na Mesa — define o quão pesado é o fluxo de decisão.
 *
 * - pnl: fluxo completo de originação (preço venda alvo, derivados, custos
 *   operacionais, margem). É o default.
 * - basis_only: fluxo leve — só captura de mercado (CBOT, vencimento, câmbio)
 *   e cálculo do basis sobre o preço do contrato. Sem preço-venda/margem/custos.
 *   Usado por Intermediação e Assessoria Espelho.
 * - assessoria: painel de assessoria pura (contconf 4) — não é trade, sem PNL.
 */
export type MesaAprovacaoMode = 'pnl' | 'basis_only' | 'assessoria'

/**
 * Configuração completa de um tipo de contrato.
 * Cada flag descreve um comportamento que antes era um if/else espalhado.
 */
export interface ContractTypeConfig {
  /** CONTCONF (id_registro na tabela tipos_contrato) */
  contconf: number
  /** Nome exato do tipo de contrato */
  nome: string
  /** Categoria para agrupamento em dashboards */
  categoria: CategoriaContrato
  /** Tipo de negócio (para tabs do form) */
  tipoNegocio: TipoNegocio
  /** Moedas que este tipo aceita */
  moedasPermitidas: Moeda[]
  /** Se o usuário pode escolher a moeda no form */
  moedaFlexivel: boolean
  /** Moeda padrão quando não há escolha */
  moedaPadrao: Moeda
  /** Como resolver o item para API */
  itemResolution: ItemResolution
  /** Item fixo (quando itemResolution === 'fixed') */
  fixedItem?: number
  /** Como resolver numerocm/numerocmadic */
  idLogic: IdLogic
  /** Estratégia de cálculo de valores */
  valueStrategy: ValueStrategy
  /** Estratégia de cálculo de datas */
  dateStrategy: DateStrategy
  /** Regra do campo pagadorcont */
  pagadorContRule: PagadorContRule
  /** Se exige campo hectares */
  requiresHectares: boolean
  /** Se exige campo comissão */
  requiresComissao: boolean
  /** Se bypass validação de quantidade/preço */
  bypassQuantidadeValidation: boolean
  /** Se conta como "venda" no resultado_compra_venda */
  contaComoVenda: boolean
  /** Se conta como "compra" no resultado_compra_venda */
  contaComoCompra: boolean
  /** Parte fixa (auto-preenchida com Innovagro): 'comprador' | 'cliente' | null */
  parteFixa?: 'comprador' | 'cliente' | null
  /** Se é contrato "a fixar" (preço definido posteriormente via fixação) */
  isAfixar?: boolean
  /** Se é componente com preço A FORMAR (sem input de preço; vértices em solicitacao_componentes) */
  precoAFormar?: boolean
  /** Modo de aprovação na Mesa (default 'pnl' quando ausente) */
  mesaAprovacao?: MesaAprovacaoMode
  /**
   * Se este tipo EMITE contrato no Viasoft (write-path ao ERP). Default false.
   * Ponto ÚNICO de flip do fail-closed do serviço: o guard SERVICO_NAO_EMITE
   * barra categoria 'servico' SALVO quando emiteViasoft===true. Só o
   * CONTRATO ASSESSORIA MESA (284, criado no Viasoft pelo CEO) o tem true;
   * qualquer outro serviço futuro nasce fail-closed até ganhar o flip.
   */
  emiteViasoft?: boolean
}

/** Metadata JSONB salvo na tabela tipos_contrato */
interface ContractMetadata {
  categoria?: string
  moedas_permitidas?: string[]
  moeda_flexivel?: boolean
  moeda_padrao?: string
  item_resolution?: string
  fixed_item?: number
  id_logic?: string
  value_strategy?: string
  date_strategy?: string
  pagador_cont_rule?: string
  requires_hectares?: boolean
  requires_comissao?: boolean
  bypass_quantidade_validation?: boolean
  conta_como_venda?: boolean
  conta_como_compra?: boolean
  parte_fixa?: 'comprador' | 'cliente' | null
  is_afixar?: boolean
  preco_a_formar?: boolean
  mesa_aprovacao?: string
  emite_viasoft?: boolean
}

// ============================================================================
// REGISTRY - HARDCODED DEFAULTS (fallback quando DB indisponível)
// ============================================================================

const CONTRACT_TYPES: readonly ContractTypeConfig[] = [
  // --- INTERMEDIAÇÃO (contconf 1, 2, 3) ---
  {
    contconf: 1,
    nome: 'INTERMEDIAÇÃO A REAL',
    categoria: 'intermediacao',
    tipoNegocio: 'Intermediacao',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'fixed',
    fixedItem: 12,
    idLogic: 'separate',
    valueStrategy: 'intermediacao',
    dateStrategy: 'intermediacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: true,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: false,
    mesaAprovacao: 'basis_only',
  },
  {
    contconf: 2,
    nome: 'INTERMEDIAÇÃO A FIXAR',
    categoria: 'intermediacao',
    tipoNegocio: 'Intermediacao',
    moedasPermitidas: ['BRL', 'USD'],
    moedaFlexivel: true,
    moedaPadrao: 'BRL',
    itemResolution: 'fixed',
    fixedItem: 12,
    idLogic: 'separate',
    valueStrategy: 'intermediacao',
    dateStrategy: 'intermediacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: true,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: false,
    isAfixar: false,
  },
  {
    contconf: 3,
    nome: 'INTERMEDIAÇÃO A DOLAR',
    categoria: 'intermediacao',
    tipoNegocio: 'Intermediacao',
    moedasPermitidas: ['USD'],
    moedaFlexivel: false,
    moedaPadrao: 'USD',
    itemResolution: 'fixed',
    fixedItem: 12,
    idLogic: 'separate',
    valueStrategy: 'intermediacao',
    dateStrategy: 'intermediacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: true,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: false,
    mesaAprovacao: 'basis_only',
  },

  // --- ASSESSORIA (contconf 4) ---
  {
    contconf: 4,
    nome: 'CONTRATO ASSESSORIA',
    categoria: 'assessoria',
    tipoNegocio: 'Assessoria',
    moedasPermitidas: ['BRL', 'USD'],
    moedaFlexivel: true,
    moedaPadrao: 'BRL',
    itemResolution: 'fixed',
    fixedItem: 13,
    idLogic: 'separate',
    valueStrategy: 'assessoria',
    dateStrategy: 'assessoria_contconf4',
    pagadorContRule: 'cliente_nome',
    requiresHectares: true,
    requiresComissao: false,
    bypassQuantidadeValidation: true,
    contaComoVenda: false,
    contaComoCompra: false,
    mesaAprovacao: 'assessoria',
  },

  // --- ASSESSORIA ESPELHO (contconf 5, 6) ---
  {
    contconf: 5,
    nome: 'ASSESORIA ESPELHO REAL',
    categoria: 'assessoria_negocios',
    tipoNegocio: 'Assessoria espelho',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'fixed',
    fixedItem: 13,
    idLogic: 'separate',
    valueStrategy: 'normal',
    dateStrategy: 'assessoria_espelho',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: false,
    mesaAprovacao: 'basis_only',
  },
  {
    contconf: 6,
    nome: 'ASSESORIA ESPELHO DOLAR',
    categoria: 'assessoria_negocios',
    tipoNegocio: 'Assessoria espelho',
    moedasPermitidas: ['USD'],
    moedaFlexivel: false,
    moedaPadrao: 'USD',
    itemResolution: 'fixed',
    fixedItem: 13,
    idLogic: 'separate',
    valueStrategy: 'normal',
    dateStrategy: 'assessoria_espelho',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: false,
    mesaAprovacao: 'basis_only',
  },

  // --- ORIGINAÇÃO (contconf 20, 21, 204, 224) ---
  {
    contconf: 20,
    nome: 'CONTRATO DE COMPRA',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_cliente',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'innovagro',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: true,
    parteFixa: 'comprador',
  },
  {
    contconf: 21,
    nome: 'CONTRATO DE VENDA',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_comprador',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: true,
    contaComoCompra: false,
    parteFixa: 'cliente',
  },
  {
    contconf: 204,
    nome: 'VENDA A FIXAR',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_comprador',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: true,
    contaComoCompra: false,
    parteFixa: 'cliente',
    isAfixar: true,
  },
  {
    contconf: 224,
    nome: 'COMPRA A FIXAR',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_cliente',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'innovagro',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: true,
    parteFixa: 'comprador',
    isAfixar: true,
  },
  {
    contconf: 244,
    nome: 'CONTRATO DE COMPRA A DOLAR',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['USD'],
    moedaFlexivel: false,
    moedaPadrao: 'USD',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_cliente',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'innovagro',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: false,
    contaComoCompra: true,
    parteFixa: 'comprador',
    isAfixar: false,
  },
  {
    contconf: 245,
    nome: 'CONTRATO DE VENDA A DOLAR',
    categoria: 'originacao',
    tipoNegocio: 'Originacao',
    moedasPermitidas: ['USD'],
    moedaFlexivel: false,
    moedaPadrao: 'USD',
    itemResolution: 'dynamic_variant',
    idLogic: 'both_comprador',
    valueStrategy: 'normal',
    dateStrategy: 'originacao',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: false,
    contaComoVenda: true,
    contaComoCompra: false,
    parteFixa: 'cliente',
    isAfixar: false,
  },

  // --- ASSESSORIA MESA (contconf 284 — SERVIÇO, assinatura mensal #214) ---
  // Contrato de PRESTAÇÃO DE SERVIÇOS da mesa (sem commodity/volume/preço).
  // contconf 284 = tipo criado no Viasoft pelo CEO (era 990 reservado pré-ERP).
  // EMITE no Viasoft como REGISTRO comercial (emiteViasoft=true) — sem fiscal
  // (o payload não tem CFOP/NCM/tributo). O guard SERVICO_NAO_EMITE continua
  // fail-closed pra qualquer OUTRO serviço; só o 284 tem o flip.
  // Não aparece no menu de tipos: entra pelo toggle Mesa|Completa DENTRO do
  // form do CONTRATO ASSESSORIA (visivel_navegacao=false na tabela).
  {
    contconf: 284,
    nome: 'CONTRATO ASSESSORIA MESA',
    categoria: 'servico',
    tipoNegocio: 'Assessoria',
    moedasPermitidas: ['BRL'],
    moedaFlexivel: false,
    moedaPadrao: 'BRL',
    itemResolution: 'fixed',
    fixedItem: 13,
    // numerocm = CONTRATANTE (cliente); numerocmadic = Innovagro (parte
    // CONTRATADA, id 1) — casa com parteFixa='comprador' e com o comprador
    // que o form já grava (numerocmadic===1).
    idLogic: 'separate',
    // Serviço: quarteto próprio (quantidade=1, valorunit=total do período,
    // precocont=mensal, qtditem=meses) — ver transformValues('servico_mesa').
    valueStrategy: 'servico_mesa',
    dateStrategy: 'assessoria_contconf4',
    pagadorContRule: 'cliente_nome',
    requiresHectares: false,
    requiresComissao: false,
    bypassQuantidadeValidation: true,
    contaComoVenda: false,
    contaComoCompra: false,
    // Innovagro é a parte CONTRATADA do serviço — auto-preenchida como
    // comprador (conceito parteFixa existente; NÃO é o hack comprador=cliente)
    parteFixa: 'comprador',
    mesaAprovacao: 'assessoria',
    // Ponto ÚNICO de flip do fail-closed: SÓ o 284 emite.
    emiteViasoft: true,
  },
] as const

// ============================================================================
// INDEX (lookup rápido por contconf - hardcoded only)
// ============================================================================

const BY_CONTCONF = new Map<number, ContractTypeConfig>(
  CONTRACT_TYPES.map(ct => [ct.contconf, ct])
)

// ============================================================================
// DB CACHE - Merge dinâmico do banco com hardcoded
// ============================================================================

/** Cache de configs vindos do DB (override sobre hardcoded) */
let dbCache = new Map<number, ContractTypeConfig>()

/** Contconfs marcados como inativos no DB */
let dbInactiveContconfs = new Set<number>()

/**
 * Mapeia id_tipo_negocio (numérico) para TipoNegocio string.
 */
function mapTipoNegocio(idTipoNegocio: number | null): TipoNegocio {
  switch (idTipoNegocio) {
    case 1: return 'Assessoria'
    case 2: return 'Intermediacao'
    case 3: return 'Originacao'
    default: return 'Originacao'
  }
}

/** Tipo das rows retornadas pela query do DB */
interface TipoContratoRow {
  id_registro: number | null
  nome: string
  metadata: ContractMetadata | null
  ativo: boolean
  id_tipo_negocio: number | null
}

/**
 * Recebe rows do DB e popula o cache interno.
 * Chamado pelo registry-loader (server-side).
 * Não importa supabase — apenas processa dados.
 */
export function loadRegistryFromDb(rows: TipoContratoRow[]): void {
  const newCache = new Map<number, ContractTypeConfig>()
  const newInactive = new Set<number>()

  for (const row of rows) {
    if (!row.id_registro) continue

    if (!row.ativo) {
      newInactive.add(row.id_registro)
      continue
    }

    const meta = row.metadata as ContractMetadata | null
    const hardcoded = BY_CONTCONF.get(row.id_registro)

    // Sem metadata.categoria e sem hardcoded = registrar apenas o nome para exibição
    if (!meta?.categoria && !hardcoded) {
      newCache.set(row.id_registro, {
        contconf: row.id_registro,
        nome: row.nome,
        categoria: 'outros' as CategoriaContrato,
        tipoNegocio: mapTipoNegocio(row.id_tipo_negocio),
        moedasPermitidas: ['BRL'],
        moedaFlexivel: false,
        moedaPadrao: 'BRL',
        itemResolution: 'fixed',
        idLogic: 'separate',
        valueStrategy: 'normal',
        dateStrategy: 'originacao',
        pagadorContRule: 'cliente_nome',
        requiresHectares: false,
        requiresComissao: false,
        bypassQuantidadeValidation: false,
        contaComoVenda: false,
        contaComoCompra: false,
      })
      continue
    }

    if (!meta?.categoria) continue // sem metadata = usa hardcoded
    newCache.set(row.id_registro, {
      contconf: row.id_registro,
      nome: row.nome,
      categoria: meta.categoria as CategoriaContrato,
      tipoNegocio: mapTipoNegocio(row.id_tipo_negocio),
      moedasPermitidas: (meta.moedas_permitidas as Moeda[]) ?? hardcoded?.moedasPermitidas ?? ['BRL'],
      moedaFlexivel: meta.moeda_flexivel ?? hardcoded?.moedaFlexivel ?? false,
      moedaPadrao: (meta.moeda_padrao as Moeda) ?? hardcoded?.moedaPadrao ?? 'BRL',
      itemResolution: (meta.item_resolution as ItemResolution) ?? hardcoded?.itemResolution ?? 'fixed',
      fixedItem: meta.fixed_item ?? hardcoded?.fixedItem,
      idLogic: (meta.id_logic as IdLogic) ?? hardcoded?.idLogic ?? 'separate',
      valueStrategy: (meta.value_strategy as ValueStrategy) ?? hardcoded?.valueStrategy ?? 'normal',
      dateStrategy: (meta.date_strategy as DateStrategy) ?? hardcoded?.dateStrategy ?? 'originacao',
      pagadorContRule: (meta.pagador_cont_rule as PagadorContRule) ?? hardcoded?.pagadorContRule ?? 'cliente_nome',
      requiresHectares: meta.requires_hectares ?? hardcoded?.requiresHectares ?? false,
      requiresComissao: meta.requires_comissao ?? hardcoded?.requiresComissao ?? false,
      bypassQuantidadeValidation: meta.bypass_quantidade_validation ?? hardcoded?.bypassQuantidadeValidation ?? false,
      contaComoVenda: meta.conta_como_venda ?? hardcoded?.contaComoVenda ?? false,
      contaComoCompra: meta.conta_como_compra ?? hardcoded?.contaComoCompra ?? false,
      parteFixa: meta.parte_fixa ?? hardcoded?.parteFixa ?? null,
      isAfixar: meta.is_afixar ?? hardcoded?.isAfixar ?? false,
      precoAFormar: meta.preco_a_formar ?? hardcoded?.precoAFormar ?? false,
      // mesaAprovacao vive só no hardcoded (não é editável no banco hoje). Sem
      // este fallback, o merge do DB dropava o campo e todo tipo com metadata
      // (ex.: espelho) voltava a 'pnl' em runtime.
      mesaAprovacao: (meta.mesa_aprovacao as MesaAprovacaoMode) ?? hardcoded?.mesaAprovacao,
      // Fail-closed: emissão só quando explicitamente true (meta OU hardcoded);
      // ausência = false = bloqueado. É o flip do guard SERVICO_NAO_EMITE.
      emiteViasoft: meta.emite_viasoft ?? hardcoded?.emiteViasoft ?? false,
    })
  }

  dbCache = newCache
  dbInactiveContconfs = newInactive
}

/**
 * Invalida o cache forçando reload no próximo acesso.
 * Chamar após POST/PUT/DELETE na API de tipos_contrato.
 */
export function invalidateRegistryCache(): void {
  dbCache = new Map()
  dbInactiveContconfs = new Set()
}

// ============================================================================
// MERGED LOOKUP — DB cache > hardcoded, respeitando inativos
// ============================================================================

/**
 * Resolve um contconf para ContractTypeConfig.
 * Prioridade: dbInactive (retorna undefined) > dbCache > hardcoded
 *
 * COERÇÃO (#214): `contconf` pode chegar como STRING — a coluna é `bigint` e o
 * Supabase/PostgREST devolve bigint como texto ("284"). Os Maps são keyed por
 * number, então `.get("284")` daria miss e o tipo cairia no layout de commodity
 * (bug do painel histórico da Assessoria Mesa). Coage aqui, no ponto ÚNICO de
 * resolução, blindando TODOS os consumidores (isServico/isComponente/etc.) — em
 * vez de depender de cada caller lembrar de `Number(contconf)`.
 */
function resolveContractType(contconf: number): ContractTypeConfig | undefined {
  const cc = Number(contconf)
  if (!Number.isFinite(cc)) return undefined
  if (dbInactiveContconfs.has(cc)) return undefined
  return dbCache.get(cc) ?? BY_CONTCONF.get(cc)
}

/**
 * Retorna todos os tipos merged (hardcoded + DB, excluindo inativos).
 */
function getMergedContractTypes(): ContractTypeConfig[] {
  const merged = new Map<number, ContractTypeConfig>()

  // 1. Hardcoded como base
  for (const ct of CONTRACT_TYPES) {
    if (!dbInactiveContconfs.has(ct.contconf)) {
      merged.set(ct.contconf, ct)
    }
  }

  // 2. DB override (sobrescreve hardcoded e adiciona novos)
  for (const [contconf, config] of dbCache) {
    if (!dbInactiveContconfs.has(contconf)) {
      merged.set(contconf, config)
    }
  }

  return Array.from(merged.values())
}

// ============================================================================
// HELPERS PÚBLICOS - Derivados do registry (merged)
// ============================================================================

/**
 * Retorna config do tipo de contrato pelo contconf.
 * Retorna undefined se não encontrado ou inativo.
 */
export function getContractType(contconf: number): ContractTypeConfig | undefined {
  return resolveContractType(contconf)
}

/**
 * Retorna config do tipo de contrato ou lança erro.
 */
export function getContractTypeOrThrow(contconf: number): ContractTypeConfig {
  const config = resolveContractType(contconf)
  if (!config) {
    const allTypes = getMergedContractTypes()
    throw new Error(
      `[CONTRACT-REGISTRY] Contconf ${contconf} não encontrado. ` +
      `Válidos: ${allTypes.map(ct => ct.contconf).join(', ')}`
    )
  }
  return config
}

/**
 * Filtra tipos por categoria.
 */
export function getContractTypesByCategory(categoria: CategoriaContrato): ContractTypeConfig[] {
  return getMergedContractTypes().filter(ct => ct.categoria === categoria)
}

/** NOMES_TIPO_CONTRATO derivado: contconf -> nome */
export function getNomesTipoContrato(): Record<number, string> {
  return Object.fromEntries(
    getMergedContractTypes().map(ct => [ct.contconf, ct.nome])
  )
}

/** Dynamic getter: contconf -> item (server-side, after ensureRegistryLoaded) */
export function getContconfToItem(): Record<number, number> {
  return Object.fromEntries(
    getMergedContractTypes()
      .filter(ct => ct.itemResolution === 'fixed' && ct.fixedItem != null)
      .map(ct => [ct.contconf, ct.fixedItem!])
  )
}

/**
 * Backward-compatible static exports.
 * Estes usam apenas hardcoded (para client-side).
 * Server-side deve usar os getters acima após ensureRegistryLoaded().
 */
export const CATEGORIAS_MAP_REGISTRY: Record<number, CategoriaContrato> = Object.fromEntries(
  CONTRACT_TYPES.map(ct => [ct.contconf, ct.categoria])
)

export const NOMES_TIPO_CONTRATO_REGISTRY: Record<number, string> = Object.fromEntries(
  CONTRACT_TYPES.map(ct => [ct.contconf, ct.nome])
)

export const CONTCONF_TO_ITEM_REGISTRY: Record<number, number> = Object.fromEntries(
  CONTRACT_TYPES
    .filter(ct => ct.itemResolution === 'fixed' && ct.fixedItem != null)
    .map(ct => [ct.contconf, ct.fixedItem!])
)

// ============================================================================
// HELPERS DE MOEDA - Derivados do registry
// ============================================================================

/**
 * Retorna moedas permitidas para um contconf.
 */
export function getMoedasPermitidasRegistry(contconf: number): Moeda[] {
  const config = resolveContractType(contconf)
  if (!config) return ['BRL']
  return [...config.moedasPermitidas]
}

/**
 * Valida se uma moeda é permitida para um contconf.
 */
export function validarMoedaRegistry(contconf: number, moeda: Moeda): boolean {
  const config = resolveContractType(contconf)
  if (!config) return moeda === 'BRL'
  return config.moedasPermitidas.includes(moeda)
}

// ============================================================================
// HELPERS DE CLASSIFICAÇÃO - Derivados do registry
// ============================================================================

/**
 * Verifica se contconf é de originação.
 */
export function isOriginacaoByContconf(contconf: number): boolean {
  const config = resolveContractType(contconf)
  return config?.categoria === 'originacao'
}

/**
 * Verifica se o contconf é CONTRATO ASSESSORIA puro (categoria 'assessoria').
 * Os espelhos (5/6) têm categoria 'assessoria_negocios' e ficam de fora — é a
 * "assessoria mesmo, não espelho".
 */
export function isAssessoriaByContconf(contconf: number): boolean {
  return resolveContractType(contconf)?.categoria === 'assessoria'
}

/**
 * Contrato de SERVIÇO (Assessoria Mesa #214 — assinatura mensal, sem
 * commodity). FAIL-CLOSED: as portas de emissão Viasoft consultam este
 * helper e RECUSAM o tipo (não existe no ERP).
 */
export function isServicoByContconf(contconf: number): boolean {
  return resolveContractType(contconf)?.categoria === 'servico'
}

/**
 * Contrato de INTERMEDIAÇÃO (contconf 1/2/3 — a real, a fixar, a dólar).
 * A comissão só faz sentido nesses tipos, então a Mesa só vê o bloco de
 * comissão quando este helper responde true (IA-1244).
 */
export function isIntermediacaoByContconf(contconf: number | null | undefined): boolean {
  if (contconf == null) return false
  return resolveContractType(contconf)?.categoria === 'intermediacao'
}

/**
 * O contconf EMITE contrato no Viasoft (write-path ao ERP)? Fail-closed:
 * default false. É o ÚNICO ponto que libera o guard SERVICO_NAO_EMITE — só o
 * CONTRATO ASSESSORIA MESA (284) tem emiteViasoft=true; qualquer outro serviço
 * segue barrado. Usado no guard das 3 portas de emissão.
 */
export function emiteViasoftByContconf(contconf: number | null | undefined): boolean {
  if (contconf == null) return false
  return resolveContractType(contconf)?.emiteViasoft ?? false
}

/**
 * Modo de aprovação na Mesa para um contconf. Default 'pnl' quando o tipo não
 * declara explicitamente. Intermediação e Espelho usam 'basis_only'; Assessoria
 * pura usa 'assessoria'.
 */
export function getMesaAprovacaoMode(contconf: number | null | undefined): MesaAprovacaoMode {
  if (contconf == null) return 'pnl'
  return resolveContractType(contconf)?.mesaAprovacao ?? 'pnl'
}

/** Atalho: o contconf usa o fluxo leve de mercado + basis na Mesa? */
export function isBasisOnlyByContconf(contconf: number | null | undefined): boolean {
  return getMesaAprovacaoMode(contconf) === 'basis_only'
}

/**
 * Verifica se o tipo de contrato conta como "venda" no resultado compra/venda.
 */
export function contaComoVenda(contconf: number): boolean {
  return resolveContractType(contconf)?.contaComoVenda ?? false
}

/**
 * Verifica se o tipo de contrato conta como "compra" no resultado compra/venda.
 */
export function contaComoCompra(contconf: number): boolean {
  return resolveContractType(contconf)?.contaComoCompra ?? false
}

/**
 * Verifica se o item precisa de resolução dinâmica (variante de produto).
 */
export function requiresDynamicItem(contconf: number): boolean {
  const config = resolveContractType(contconf)
  return config?.itemResolution === 'dynamic_variant'
}

/**
 * Verifica se o contconf é do tipo "a fixar" (preço definido posteriormente).
 * Lê do DB metadata (is_afixar) ou fallback hardcoded.
 */
export function isAfixarByContconf(contconf: number): boolean {
  const config = resolveContractType(contconf)
  return config?.isAfixar ?? false
}

/**
 * Contconfs que entram na FIXAÇÃO por câmbio, além dos "a fixar" de preço.
 * Contrato de compra a dólar (244): o preço em dólar já está fixado; o que se
 * fixa é a cotação (câmbio). Por isso ele aparece nos filtros de fixação mesmo
 * NÃO sendo `isAfixar` (o preço não é indefinido). Não flipar `isAfixar` global —
 * isso afetaria comissão/preço-opcional; aqui é só elegibilidade de fixação.
 */
export const CONTCONF_FIXACAO_CAMBIO: number[] = [244]

/**
 * Verifica se o contconf é componente com preço A FORMAR (#214): o form não
 * pede preço (precocont nasce NULL) e os vértices de formação (nível de bolsa,
 * câmbio, custo, prêmio) vivem em solicitacao_componentes. Lê do DB metadata
 * (preco_a_formar); fail-closed quando ausente.
 */
export function isPrecoAFormarByContconf(contconf: number): boolean {
  const config = resolveContractType(contconf)
  return config?.precoAFormar ?? false
}
